// Product surface registry — THE single place the top-level information
// architecture lives ([[ia-consolidation]]: answer → proof → tools, grouped by
// See the work · Plan the capacity · Catch the risk).
//
// Consumers:
//   - Sidebar.tsx renders its nav from `PRODUCT_SURFACES` (sidebar: true, in
//     this order).
//   - The guided tour's canonical storyboard (server/src/lib/defaultTourSteps.ts)
//     must visit the `tourAnchors` surfaces IN THIS ORDER — enforced by
//     server/src/__tests__/lib/defaultTourSteps.test.ts, which PARSES THIS FILE
//     TEXTUALLY. Keep one surface per line and keep the `route:` /
//     `tourAnchors:` field shapes stable, or that test breaks.
//   - GuidedTour.tsx derives each step's pillar chip via `pillarForAnchor`.
//
// Why this exists: the tour storyboard drifted silently for three weeks after
// the 2026-06-09 IA consolidation because the sidebar order, the tour order,
// and the anchor tags lived in three unrelated places. Reordering the IA now
// means editing THIS array — the contract tests then force the tour (and any
// future IA-telling surface) to follow in the same change.

import {
  Sparkles, BarChart3, Gauge, ClipboardList, Folder, Database, Plug,
  type LucideIcon,
} from 'lucide-react';

export type Pillar = 'see' | 'plan' | 'catch';

export interface ProductSurface {
  /** Stable internal id — never user-facing. */
  id: string;
  /** Top-level route (no query string). */
  route: string;
  /** Sidebar / display label (UX terminology rules apply). */
  label: string;
  icon: LucideIcon;
  /** Which slogan pillar the surface serves; null = utility surface. */
  pillar: Pillar | null;
  /** Renders in the global sidebar nav. */
  sidebar: boolean;
  /**
   * data-tour anchor tag(s) of the surface's tour stop(s), in the order the
   * tour visits them. Empty = the guided tour does not stop here.
   */
  tourAnchors: string[];
}

// One surface per line — parsed textually by defaultTourSteps.test.ts.
//
// Per-surface history (moved here from Sidebar.tsx when the nav became
// registry-driven):
//   - The Brief is the answer home; the old "Avium Intelligence" bucket was
//     dissolved by the IA consolidation and its pieces re-homed as top-level
//     surfaces.
//   - Explore was demoted from the sidebar 2026-06-11 (user direction) — it
//     lives as the last tab of Reports (/reports?tab=explore); /explore
//     redirects there.
//   - 'Files' (not 'Documents') matches the page title + the "Avium Files"
//     brand name, and avoids colliding with the Document → Stories intake's
//     mental model ("upload my meeting document" ≠ file storage).
//   - Data Sources is reached via Settings, not the global sidebar
//     (sidebar: false). It OPERATES connected sources (sync · push · export ·
//     governance) and carries NO tour stop: the 2026-06-11 split moved "add a
//     source" to /connect, and the tour's activation close kept pointing at
//     /data for six weeks — so the step promising "Jira, Azure DevOps, Asana,
//     or an Excel/CSV upload" landed on the management grid instead of the
//     picker (Brian, 2026-07-21: step 8 "does not accurately show the import
//     your data"). Its `multi-source-import` anchor stays rendered on the hub;
//     it just no longer claims the storyboard's data stop.
//   - Connect (the source picker wizard) is the tour's activation close and
//     therefore LAST in this array — the storyboard order is this array's
//     order.
// 'rollup' and 'my-week' were REMOVED 2026-07-20 (Work Center consolidation
// slice W1, docs/design/work-center-hifi.html state A, approved 2026-07-20) —
// both surfaces re-homed into Workload's own tab strip (the Epics tab and the
// My Week tab, PipelinePage.tsx); their old routes redirect there (App.tsx's
// RollupRedirect / MyWeekRedirect). Neither carried a tour stop, so the
// guided-tour storyboard needed no change.
export const PRODUCT_SURFACES: ProductSurface[] = [
  { id: 'brief',           route: '/brief',           label: 'The Brief',       icon: Sparkles,      pillar: 'catch', sidebar: true,  tourAnchors: ['ai-briefing', 'kg-signals-rail', 'signals-engine'] },
  { id: 'reports',         route: '/reports',         label: 'Reports',         icon: BarChart3,     pillar: 'see',   sidebar: true,  tourAnchors: ['reports-home'] },
  { id: 'capacity',        route: '/capacity',        label: 'Capacity',        icon: Gauge,         pillar: 'plan',  sidebar: true,  tourAnchors: ['capacity-overview'] },
  { id: 'workload',        route: '/pipeline',        label: 'Workload',        icon: ClipboardList, pillar: 'see',   sidebar: true,  tourAnchors: ['workload-board'] },
  { id: 'files',           route: '/files',           label: 'Files',           icon: Folder,        pillar: null,    sidebar: true,  tourAnchors: [] },
  { id: 'data-sources',    route: '/data',            label: 'Data Sources',    icon: Database,      pillar: 'see',   sidebar: false, tourAnchors: [] },
  { id: 'connect',         route: '/connect',         label: 'Connect a source', icon: Plug,         pillar: 'see',   sidebar: false, tourAnchors: ['connect-sources'] },
];

// Every data-tour anchor in the product, in one place. JSX sites use
// `data-tour={TOUR_ANCHOR.x}` instead of scattering string literals — one file
// to see them all, and a typo becomes a compile error instead of a silently
// centered tooltip.
export const TOUR_ANCHOR = {
  sidebarNav: 'sidebar-nav',
  adminCustomization: 'admin-customization',
  createTicket: 'create-ticket',
  filterBar: 'filter-bar',
  workloadBoard: 'workload-board',
  jiraWriteback: 'jira-writeback',
  multiSourceImport: 'multi-source-import',
  connectSources: 'connect-sources',
  capacityOverview: 'capacity-overview',
  aiBriefing: 'ai-briefing',
  signalsEngine: 'signals-engine',
  briefingGraph: 'briefing-graph',
  mapGraph: 'map-graph',
  kgFocus: 'kg-focus',
  kgSignalsRail: 'kg-signals-rail',
  reportsHome: 'reports-home',
} as const;

export type TourAnchor = (typeof TOUR_ANCHOR)[keyof typeof TOUR_ANCHOR];

export const PILLAR_LABEL: Record<Pillar, string> = {
  see: 'See the work',
  plan: 'Plan the capacity',
  catch: 'Catch the risk',
};

/**
 * Resolve the pillar a tour step belongs to, from either a bare anchor tag
 * ("ai-briefing") or a full target selector ('[data-tour="ai-briefing"]').
 * Returns null for welcome/closing beats, unknown anchors, and anchors that
 * belong to no pillar-bearing surface — the tooltip simply renders no chip.
 */
export function pillarForAnchor(anchorOrTarget: string | null | undefined): Pillar | null {
  if (!anchorOrTarget) return null;
  const tag = anchorOrTarget.match(/\[data-tour="([^"]+)"\]/)?.[1] ?? anchorOrTarget;
  for (const s of PRODUCT_SURFACES) {
    if (s.tourAnchors.includes(tag)) return s.pillar;
  }
  return null;
}
